const THROUGHPUT_THRESHOLD_MBPS = 1;
const MIN_BANDWIDTH_PROBE_BYTES = 32 * 1024;
const CACHE_TTL_MS = 60_000;
const BANDWIDTH_PROBE_PATH = "/icons/logo512.png";

export const VIDEO_MODE_DISABLED_MESSAGE =
  "Your connection is too slow for video. Please use Audio Mode.";

export type NetworkQualityResult = {
  isVideoModeAvailable: boolean;
  rttMs: number | null;
  throughputMbps: number | null;
  reason?: string;
};

export type NetworkMeasurement = {
  durationMs: number;
  transferBytes?: number | null;
};

const AVAILABLE_RESULT: NetworkQualityResult = {
  isVideoModeAvailable: true,
  rttMs: null,
  throughputMbps: null,
};

let cachedQuality: { result: NetworkQualityResult; at: number } | null = null;

export const clearNetworkQualityCache = () => {
  cachedQuality = null;
};

export const getCachedNetworkQuality = (): NetworkQualityResult | null => {
  if (!cachedQuality) return null;
  if (Date.now() - cachedQuality.at > CACHE_TTL_MS) {
    cachedQuality = null;
    return null;
  }
  return cachedQuality.result;
};

export const setCachedNetworkQuality = (result: NetworkQualityResult) => {
  cachedQuality = { result, at: Date.now() };
};

export const measureThroughputMbps = (
  transferBytes: number,
  durationMs: number,
): number | null => {
  if (durationMs <= 0 || transferBytes < MIN_BANDWIDTH_PROBE_BYTES) return null;
  const bitsPerSecond = (transferBytes * 8) / (durationMs / 1000);
  return bitsPerSecond / 1_000_000;
};

const blockedResult = (
  rttMs: number | null = null,
  throughputMbps: number | null = null,
): NetworkQualityResult => ({
  isVideoModeAvailable: false,
  rttMs,
  throughputMbps,
  reason: VIDEO_MODE_DISABLED_MESSAGE,
});

export const evaluateNetworkQuality = (
  measurement?: NetworkMeasurement | null,
): NetworkQualityResult => {
  if (!measurement) return AVAILABLE_RESULT;

  const throughputMbps = measureThroughputMbps(
    measurement.transferBytes ?? 0,
    measurement.durationMs,
  );

  // Fail-open when throughput cannot be measured (avoids blocking on API latency).
  if (throughputMbps === null) return AVAILABLE_RESULT;

  if (throughputMbps < THROUGHPUT_THRESHOLD_MBPS) {
    return blockedResult(measurement.durationMs, throughputMbps);
  }

  return {
    isVideoModeAvailable: true,
    rttMs: measurement.durationMs,
    throughputMbps,
  };
};

const probeDownloadBandwidth = async (): Promise<NetworkMeasurement | null> => {
  const baseUrl = process.env.PUBLIC_URL ?? "";
  const url = `${baseUrl}${BANDWIDTH_PROBE_PATH}?_=${Date.now()}`;
  const startedAt = performance.now();

  try {
    const response = await fetch(url, { cache: "no-store" });
    if (!response.ok) return null;

    const buffer = await response.arrayBuffer();
    return {
      durationMs: performance.now() - startedAt,
      transferBytes: buffer.byteLength,
    };
  } catch {
    return null;
  }
};

export const probeNetworkQuality = async (options?: {
  bypassCache?: boolean;
  onProgress?: (result: NetworkQualityResult) => void;
}): Promise<NetworkQualityResult> => {
  if (!options?.bypassCache) {
    const cached = getCachedNetworkQuality();
    if (cached) return cached;
  }

  if (typeof navigator !== "undefined" && !navigator.onLine) {
    const result = blockedResult();
    setCachedNetworkQuality(result);
    options?.onProgress?.(result);
    return result;
  }

  const bandwidthMeasurement = await probeDownloadBandwidth();
  const result = evaluateNetworkQuality(bandwidthMeasurement);

  setCachedNetworkQuality(result);
  options?.onProgress?.(result);
  return result;
};

export const subscribeToNetworkInformationChanges = (
  listener: () => void,
): (() => void) => {
  if (typeof navigator === "undefined") {
    return () => undefined;
  }

  const connection = (
    navigator as Navigator & {
      connection?: {
        addEventListener?: (type: "change", listener: () => void) => void;
        removeEventListener?: (type: "change", listener: () => void) => void;
      };
    }
  ).connection;

  if (!connection?.addEventListener) {
    return () => undefined;
  }

  connection.addEventListener("change", listener);
  return () => connection.removeEventListener?.("change", listener);
};
